/**
 * 🚀 발주화면 과 모달과의 프로토콜
 * 모달을 띄울때 ORDER 정보를 모달에 전달한다. 이정보는 g5_orders 테이블의 정보이다.
 * 그런데 테이블 구조와는 무관하게 별개로 모달과 값을 주고 받아야할때 $p, $r, $t 오브젝트를 ORDER 에 담아서 전달한다.
 * ✅ $p : 발주화면 ➡️ 모달
 * ✅ $r : 모달 ➡️ 발주화면
 * ✅ $t : 발주화면 ➡️ 서버
 *
 * 소속(업체)별 차이는 ./Sosok/* 클래스에 응집한다.
 * 신규 업체 추가 = 새 전략 클래스 + Registry 등록 1줄.
 *
 * 분할 구성:
 *   Baljoo.js  — 라이프사이클 + BS 단계 흐름 + 시간/긴급 + 가격
 *   Render.js  — print 패밀리 (화면 그리기)
 *   Submit.js  — 검증·발주 제출
 *   Sosok/     — 소속별 전략
 *   Modal/     — BS 모달들
 */
import { getSosokStrategy } from './Sosok/Registry';
import Render from './Render';
import Submit from './Submit';
import Util from '../../Common/Util';

/**
 * 오더 칸 새 이름 → 발주 폼(요청) 키. 정본은 PHP App\Services\Order\OrderColumns::legacyMap().
 *
 * 서버는 기존 오더(수정·추가발주·오더복사)와 신규 기본값을 **오더 칸 새 이름**으로 ORDER 에 싣는다.
 * 그런데 발주 폼은 ORDER 를 통째로 /m/ajax/baljoo·/m/ajax/price 요청으로 보내고, 서버는 요청을
 * **옛 이름**(howtopay·contract_address …)으로 받는다. 그래서 진입 때 한 번 폼 키로 옮겨 두고,
 * 이후 폼·모달 코드는 요청 키만 쓴다.
 */
const ORDER_FORM_KEYS = {
    // 헤더 이름 변경
    order_owner_num: 'order_mem_ownernum', pay_method: 'howtopay', site_address: 'contract_address',
    notify_started_at: 'order_regtime', is_return_job: 'is_retun_job', updated_at: 'uptime', site_latlng: 'mem_basepoint',
    worker_owner_num: 'worker_ownernum', sign_days: 'sign_howlong_int', was_today_order: 'is_today',
    bill_tax_num: 'bill_print_tax', soo_bill_issued: 'tax_printed_to_giver', bal_bill_received: 'is_easy_order',
    bal_bill_issued: 'easy_reforderno',
    // g5_order_sites
    work_content: 'car_worktype', bal_owner_name: 'order_onwer_name', bal_owner_phone: 'order_onwer_phone',
    client_phone: 'contract_number', site_phone: 'contract_fieldnumber', client_name: 'contract_name',
    request_note: 'contract_message', admin_memo: 'order_memo',
    // g5_order_dispatches
    owner_phone: 'work_onwer_phone', direct_type: 'worker_directtype', car_plate: 'worker_carplate',
    driver_name: 'worker_name', owner_name: 'worker_owner_name', car_num: 'worker_carnum',
    driver_phone: 'worker_directphone', accepted_at: 'worker_regdate', owner_sosok: 'worker_sosok',
    car_vnum: 'car_uniqnum', bal_call_confirmed: 'callhasmade', matched_odr: 'worker_levels',
    pushed_mb_no: 'ref_push_num', driver_vnum: 'driver_uniqnum', hold_mb_no: 'worker_hold',
    // g5_order_match_states
    next_push_gap_sec: 'last_setSec', push_progress: 'order_push_cntnum', group_now: 'togo_now',
    group_all: 'togo_all', direct_wait_mode: 'is_directwait', is_direct_confirmed: 'is_select_confirm',
    // g5_order_payments
    add_hour: 'car_workaddhor', add_hour_type: 'car_workaddtype', add_hour_price: 'car_add_hr_price',
    parking_fee: 'car_parking_pee', is_paid: 'work_payment', pay_result: 'howtopay_done',
    card_ref_num: 'paycard_refnum', bal_contract_pay: 'pay_user_input', soo_contract_pay: 'pay_user_input2',
    paid_at: 'payed_date', bal_token_credited: 'counter_baljoo', soo_token_deducted: 'counter_soojoo',
    // g5_order_completions
    confirm_addr: 'wc_addr', confirm_co_name: 'wc_name', confirm_phone: 'wc_phone', done_at: 'done_time',
    sign_img_id: 'sign_id', confirm_img_id: 'img_id',
};

export default class Baljoo {

    constructor() {
        this.isSubmitted = false; // 중복전송방지를 위한 변수 ( 오더가 두번발주 되는 경우 방지 )
        this.isQuickClicked = false; // 긴급버튼을 유저가 명시적으로 눌렀는지 여부 (명시적으로 누르지 않아도 긴급으로 설정될수있다)
        this.startTime = null; // 도착시간을 선택한 시간 (경과시간을 체크하기위한 시간)
        this.strategy = null; // 소속별 발주 전략 (initialize 시 결정)
        // 단계 순서 (앞 단계 적용 시 자동으로 다음 단계 BS 가 열림)
        this.stepOrder = ['car', 'workday', 'worktime', 'howtopay', 'howlong', 'option'];
        this.stepSkip = [];
        this.stepModals = {
            'car':      '$j.F.Baljoo.ModalCarType',
            'workday':  '$j.F.Baljoo.ModalWorkDay',
            'worktime': '$j.F.Baljoo.ModalWorkTime',
            'howtopay': '$j.F.Baljoo.ModalHowToPay',
            'howlong':  '$j.F.Baljoo.ModalHowLong',
            'option':   '$j.F.Baljoo.ModalOption',
        };
        // 분할 헬퍼
        this.renderer  = new Render(this);
        this.submitter = new Submit(this);
    }

    initialize() {
        // 발주화면 토스트는 상단. 모바일 기본(하단)이면 하단 고정버튼(발주·다음)을 가린다.
        // offset 58 = 고정헤더(55px) 바로 아래 (기본 76 은 한 칸 내려와 있다)
        $j.Ui.Toast.defaults({ place: 'top', offset: 58 });
        this.initData();
        if (typeof ORDER !== 'undefined' && !ORDER['order_sosok']) {
            ORDER['order_sosok'] = MEMBER['sosok'];
        }
        this.strategy = getSosokStrategy(ORDER['order_sosok']);
        this.initPage();
        this.initHandler();
    }

    /**
     * BS 모달이 적용(apply) 되었을 때의 후처리.
     * 데이터 반영 + 화면 갱신 + 자동 다음단계 진행.
     */
    onModalApply = (id, data) => {
        console.log('baljoo.onModalApply', id, data);

        // 차종변경
        let isCarChanged = false;
        if (id == 'car' && ORDER['car_type'] != data['car_type']) {
            isCarChanged = true;
        }

        // 도착시간 변경 감지용 (긴급 해제 판단)
        const prevWorkTime = ORDER['work_time'];

        // 팝업에서 수정한 데이터 반영
        //
        // data 는 openBS 가 찍은 ORDER 통째 스냅샷이라, 모달이 건드리지도 않은 필드까지 되쓴다.
        // 그런데 아래 calcPrice 는 ajax 이고 nextStepBS 는 동기라, **다음 모달의 스냅샷은 언제나
        // calcPrice 응답보다 먼저 찍힌다.** 서버가 계산해 넣는 값을 그대로 두면 그 다음 apply 가
        // 옛 값으로 되돌린다 — 저단가로 내려간 수수료가 되살아났다가(=skyLowFeeForced 소진)
        // 다시 0 으로 롤백돼 영영 안 돌아오는 사고가 실제로 있었다 (오더 509404).
        // car_price/car_price_bonsa 는 Option·Price 모달이 직접 계산하므로 스냅샷 값이 맞다.
        delete data['is_fee'];
        delete data['sky_low_limit'];
        // $r·onApply 는 $j.Ui.BS.applySnapshot 이 뺀다 (모든 화면 공통 규칙).
        // 지정 정보는 .input_phone 의 onApply 가 ORDER.$r 에 직접 머지한다.
        $j.Ui.BS.applySnapshot(ORDER, data);

        // 차종이 변경되면 초기화
        if (isCarChanged) {
            ORDER['car_option'] = '';
            ORDER['car_jungo'] = '';
            ORDER['car_height'] = '';
            ORDER['move_distance'] = '';
            ORDER['car_price'] = 0;
            ORDER['car_range'] = '';
            ORDER['car_price_bonsa'] = 0;
            if (ORDER['car_type'] == '리프트') {
                ORDER['car_option'] = '왕복';
            }
            this.print('option');
        }

        // 화면 업데이트
        this.print(id);

        if (['car', 'howlong', 'option', 'worktime'].includes(id)) {
            this.calcPrice();
        }

        if (id == 'workday' || id == 'worktime') {
            // 도착시간을 바꾸면 긴급은 풀린다. (긴급버튼이 잡아준 시간을 사용자가 손댄 것이므로)
            if (ORDER['work_time'] != prevWorkTime) {
                this.offQuick();
            } else {
                this.applyQuickTime();
            }
            this.startTime = dayjs();
        }

        ORDER.$p = {};

        // 자동 다음단계 진행
        if (this.stepOrder.includes(id)) {
            this.nextStepBS(id, 1);
        }
    }

    // 설정한 시간이 긴급인 경우 긴급처리를 해주고 긴급이 아니면 일반으로 해준다.
    applyQuickTime() {
        // 수동긴급이 설정되어있고, 날짜가 오늘이라면 긴급설정은 그대로 유지한다.
        if (this.isManualQuickTime() && dayjs(ORDER['work_time']).format('YYYY-MM-DD') == dayjs().format('YYYY-MM-DD')) {
            return;
        }
        // 작업시간이 현재시간 기준 60분 이내이면
        if (this.isQuickTime(ORDER['work_time'])) {
            ORDER['is_quick'] = 1;
        } else {
            this.isQuickClicked = false; // 수동긴급 해제시킨다.
            ORDER['is_quick'] = 0;
        }
        this.print('quick');
    }

    /**
     * 단계 BS 를 띄운다.
     */
    openBS = (id) => {
        const className = this.stepModals[id];
        if (!className) return;

        // prev/next 가용 여부 (skip 단계는 건너뛰고 가용한 인접 단계 찾기)
        const findStep = (start, dir) => {
            let i = start + dir;
            while (i >= 0 && i < this.stepOrder.length) {
                if (!this.stepSkip.includes(this.stepOrder[i])) return this.stepOrder[i];
                i += dir;
            }
            return null;
        };
        const idx = this.stepOrder.indexOf(id);
        const prev = idx >= 0 ? findStep(idx, -1) : null;
        const next = idx >= 0 ? findStep(idx, +1) : null;
        $j.Ui.BS.open({
            target:      className,
            fixedHeight: true,
            headerBg:    'gray',
            layout:      'classic',   // 발주 단계는 종전 헤더 모양(가운데 제목 · 양옆 ‹ ›) 그대로
            onPrev:      prev ? () => this.openBS(prev) : null,
            onNext:      next ? () => this.openBS(next) : null,
            data:        Object.assign({}, ORDER, {
                onApply: (data) => this.onModalApply(id, data),
            }),
        });
    }

    /**
     * 입력안된 단계 BS 띄우기
     * @param isAlert 경고창여부
     */
    openRequiredSlide = (isAlert = false) => {
        const self = this;
        if (!this.isHowtopaySet()) {
            if (isAlert) {
                Util.swalert("결제책임(결제날짜)을 선택해주세요", () => { self.openBS('howtopay'); });
            } else {
                self.openBS('howtopay');
            }
        } else if (!ORDER['car_workhour']) {
            self.openBS('howlong');
        } else if (!ORDER['car_range']) {
            if (isAlert) {
                Util.swalert("장비 종류를 선택하셔서 가격을 설정해 주세요", () => { self.openBS('option'); });
            } else {
                self.openBS('option');
            }
        } else if (!ORDER['contract_address']) {
            if (isAlert) {
                Util.swalert('현장주소를 다시 선택해주세요', () => { self.openMap(); });
            } else {
                self.openMap();
            }
        }
    }

    /**
     * 다음 단계 BS 진행
     * @param id 현재 단계 id
     * @param diff 몇단계 건너띌거냐
     */
    nextStepBS = (id, diff = 1) => {
        // 관리자 수정모드에서는 다음단계 진행하지 않고 BS 만 닫는다.
        if (editMode == 'myorderedit' && G5['isAdmin']) {
            $j.Ui.BS.close();
            return;
        }

        const idx = this.stepOrder.indexOf(id);
        if (idx === -1) return;

        let nextIdx = idx + diff;
        // skip 처리
        while (nextIdx < this.stepOrder.length && this.stepSkip.includes(this.stepOrder[nextIdx])) {
            nextIdx++;
        }
        const nextId = this.stepOrder[nextIdx];
        if (nextId) {
            this.openBS(nextId);
        } else {
            this.openMap();
        }
    }

    /**
     * 도착시간 세팅
     */
    setWorkTime = (work_time) => {
        ORDER['work_time'] = work_time;
        ORDER['work_start_h'] = dayjs(work_time).format('HH');
        ORDER['work_start_m'] = dayjs(work_time).format('mm');
        ORDER['work_day'] = dayjs(work_time).format('YYYY-MM-DD');
    }

    /**
     * 장비대금을 계산한다.
     */
    calcPrice = () => {
        const self = this;
        const data = {
            'car_type':        ORDER['car_type'],// 업종
            'car_range':       ORDER['car_range'],// 범위
            'car_workhour':    ORDER['car_workhour'],// 작업시간
            'car_workdaytype': ORDER['car_workdaytype'],// 작업시간단위
            'car_workaddtype': ORDER['car_workaddtype'],// 추가시간
            'car_workaddhor':  ORDER['car_workaddhor'],//추가작업시간단위
            'car_option':      ORDER['car_option'],// 옵션
            'work_start_h':    ORDER['work_start_h'],//시작시간
            'work_day':        ORDER['work_day'],//작업일
            'sosok':           ORDER['order_sosok'],
            // 저단가 하한은 어플발주만 — 수정이면 원 오더 종류, 신규·복사는 버튼 전이라 빈 값(=대상)
            'order_type':      editMode == 'myorderedit' ? ORDER['order_type'] : '',
            // 회차 오더는 저단가 대상이 아니다 — 수정이면 그 사실도 보낸다. 안 보내면 서버가 돌려준 하한이
            // 페이지 진입값(skyLowState=0)을 덮어 결제책임만 바꿔도 '저단가 발주입니다' 가 뜬다
            'is_return_job':   editMode == 'myorderedit' ? ORDER['is_return_job'] : 0,
        };
        $j.F.Car.calcPrice(data, function (out) {
            ORDER['car_price'] = out['price'];
            ORDER['car_price_standard'] = out['price'];
            ORDER['car_price_bonsa'] = out['bonsa_price'];
            ORDER['sky_low_limit'] = out['sky_low_limit'];// 스카이 저단가 하한액 (0:대상아님)
            self.priceEditedByUser = false;// 서버가 다시 계산한 값 = 사용자가 친 게 아니다
            self.print('price');
            self.checkSkyLowPrice();
        });
    }

    /**
     * 스카이 저단가 (2026-09-01~) — 하한 미만이면 금액칸을 경고 모양으로 바꾸고 수수료없음으로 내린다.
     *
     * 발주를 막지는 않는다. 막는 것은 비율형 저단가(isLowOrderPrice)뿐이다.
     * 알림을 따로 띄우지 않는 이유 — **문제가 있는 값이 금액칸 그 자리에 있다.**
     * 거기에 표시하면 사용자가 고칠 자리와 경고가 같은 곳이라, 지나가는 알림보다 오래 남는다.
     * (최종 경고는 발주 직전 확인 다이얼로그 — Submit.checkSubmit)
     *
     * 수수료는 서버(OrderCreationService)가 어차피 0 으로 저장하므로, 화면도 같은 값을 보여준다.
     * 우리가 내린 것만 되돌린다 — 사용자가 직접 끈 수수료는 금액을 올려도 켜주지 않는다.
     */
    checkSkyLowPrice = () => {
        const isLow = this.isSkyLowPrice();
        // 금액칸 경고는 업체발주자에게 띄우지 않는다 — 계약단가라 거의 다 걸려서 경고가 상시가 된다.
        // 수수료는 아래에서 그대로 내린다 (서버가 소속을 안 가리고 0 으로 저장한다).
        $('#input_price').closest('.price').toggleClass('is-lowprice', $j.F.Car.isSkyLowWarn(ORDER));

        if (!isLow) {
            if (this.skyLowFeeForced) {// 하한 위로 올라왔으니 우리가 내린 수수료를 되돌린다
                this.skyLowFeeForced = false;
                ORDER['is_fee'] = 1;
                this.print('fee');
            }
            return;
        }

        if (Util.int(ORDER['is_fee'])) {
            this.skyLowFeeForced = true;
            ORDER['is_fee'] = 0;
            this.print('fee');
        }
    }

    /**
     * 수수료 버튼 누를때
     */
    toggleFee = () => {
        let isFee = Util.int(ORDER['is_fee']);
        ORDER['is_fee'] = (isFee + 1) % 2;
        this.print('fee');
        this.checkSkyLowPrice();// 저단가면 수수료를 다시 켤 수 없다 (서버도 0 으로 저장한다)
    }

    /**
     * 수수료없음으로
     */
    offFee() {
        ORDER['is_fee'] = 0;
        this.print('fee');
    }

    /**
     * 긴급 버튼 누를때
     */
    toggleQuick = () => {
        console.log('toggleQuick');
        let isQuick = ORDER['is_quick'];
        const n = (isQuick + 1) % 2;
        ORDER['is_quick'] = n;

        this.setQuickTime(n);
        this.print('quick');
        this.print('workday');
        this.print('worktime');
    }

    /**
     * 긴급 해제 (도착시간을 직접 바꿨을 때 — 시간이 60분 이내여도 자동 긴급으로 되돌리지 않는다)
     */
    offQuick = () => {
        this.isQuickClicked = false;
        ORDER['is_quick'] = 0;
        this.print('quick');
    }

    /**
     * 도착시간이 긴급인지 일반인지 ( 시간 이내이면 긴급 )
     * @returns {boolean} true(긴급), false(일반)
     */
    isQuickTime(work_time) {
        let mm = dayjs(work_time).diff(dayjs(), 'minute');
        if (10 <= mm && mm <= 60) return true;
        return false;
    }

    /**
     * 긴급 옵션 설정시 시간 세팅 (현재시간+40분, 10분단위로 끊고 올림으로 처리)
     * @param n 1(긴급), 0(일반)
     */
    setQuickTime = (n) => {
        const now = dayjs();
        let work_time = '';
        let minutes = null;
        let result = null;

        if (n == 1) { // 긴급일 때 ( 10분단위올림 + 40분증가 )
            this.isQuickClicked = true;
            this.startTime = dayjs();
            minutes = now.minute();
            const extra = 10 - (minutes % 10);
            const rounded = now.add(extra === 10 ? 0 : extra, 'minute');
            result = rounded.add(40, 'minute');
        } else {// 긴급아닐때 ( 10분단위 내림 )
            this.isQuickClicked = false;
            this.startTime = null;
            minutes = now.minute();
            const floored = minutes - (minutes % 10);
            result = now.minute(floored);
        }

        work_time = result.format('YYYY-MM-DD HH:mm:00');
        this.setWorkTime(work_time);
    }

    /**
     * 수동으로 긴급 버튼 눌렀는지 여부
     */
    isManualQuickTime = () => {
        return this.isQuickClicked;
    }

    /**
     * 데이터 초기화
     */
    initData() {
        this.toFormKeys(ORDER);
        ORDER.$p = {};// 발주화면 -> 모달
        ORDER.$t = {};// 발주화면 -> 서버
        ORDER.$r = {};// 모달 -> 발주화면

        ORDER.$p.editMode = editMode;

        if (editMode == 'myorderedit') {
            // 수정 진입 시점의 값 — 확인창이 '무엇이 바뀌는지' 를 이걸로 만든다.
            // 캐시가 움직이는 축만 담는다 (작업시간·주소 등은 서버 '오더수정' 로그가 남긴다)
            ORDER['_car_price'] = ORDER['car_price'];// 원래 가격
            ORDER['_car_price_bonsa'] = ORDER['car_price_bonsa'];// 원래 업체 가격
            ORDER['_is_fee'] = ORDER['is_fee'];
            ORDER['_howtopay'] = ORDER['howtopay'];
        }
    }

    /**
     * 서버가 준 오더 칸 새 이름을 발주 폼(요청) 키로 옮긴다 — 제자리에서 고친다. ORDER_FORM_KEYS 참고.
     * 폼 키가 이미 있으면(AI 프리필 등) 그대로 둔다. bal_call_confirmed(1/0) 는 옛 'YES'/'NO' 로.
     */
    toFormKeys(order) {
        Object.keys(ORDER_FORM_KEYS).forEach((k) => {
            if (!(k in order)) return;
            const old = ORDER_FORM_KEYS[k];
            if (!(old in order)) {
                order[old] = (k === 'bal_call_confirmed') ? (Util.int(order[k]) ? 'YES' : 'NO') : order[k];
            }
            delete order[k];
        });
        // work_start_h/m 는 오더 칸에서 없어졌다(HOUR/MINUTE(work_time)). 폼·가격계산 요청은 아직 이 키를 쓴다.
        if (order['work_time'] && order['work_start_h'] == null) {
            order['work_start_h'] = dayjs(order['work_time']).format('HH');
            order['work_start_m'] = dayjs(order['work_time']).format('mm');
        }
        return order;
    }

    /**
     * 관리자 전용 그룹(.admin-group): 내부에 보이는 항목이 하나도 없으면 박스('관리자 전용' 라벨)도 숨긴다.
     * 내부 항목(머큐리 보험주소 등)은 소속/보험 여부에 따라 동적으로 토글되므로 MutationObserver 로 추종.
     * 처음엔 닫혀 있으므로(_baljoo.scss) 초기 깜빡임(FOUC)이 없다 — 보일 항목이 생길 때만 block 으로 연다.
     */
    _bindAdminGroupAutohide() {
        const el = document.querySelector('.admin-group');
        if (!el) return;
        const $ag = $(el);
        const sync = () => {
            const hasVisible = $ag.children().toArray().some(c => $(c).css('display') !== 'none');
            const want = hasVisible ? 'block' : 'none';
            if ((el.style.display || '') !== want) $ag.css('display', want);
        };
        sync();
        new MutationObserver(sync).observe(el, { attributes: true, attributeFilter: ['style', 'class'], childList: true, subtree: true });
    }

    /**
     * 화면 초기화
     */
    initPage() {
        // 관리자 전용 그룹(.admin-group)은 blade 에서 관리자에게만 렌더된다 (일반회원은 DOM 자체가 없음).
        // 여기서는 렌더된 경우에만 자동 노출/숨김을 붙인다 — 내부 항목이 다 숨김이면 박스도 숨긴 채로 둔다.
        this._bindAdminGroupAutohide();
        this.updateHowtopayUI();// 결제책임, 수수료여부 보이기 / 숨기기
        // 소속별 화면 초기화 (필드 노출/숨김)
        this.strategy && this.strategy.onInitPage();

        // 지정영역 안보이게
        if (editMode == 'myorderedit' && ORDER['order_type'] != 92 && G5['isAdmin']) {
            $('.assign-button').hide();
            $('.assign-info').hide();
            $('.assign-extra').hide();
        }
        // 모든 저장,전송 버튼 숨기기
        $('#baljoo-send-area button').each(function () {
            $(this).hide();
        });
        // 관리자 오더수정 모드로 들어왔을때
        if (editMode == 'myorderedit' && ORDER['order_type'] != 92 && G5['isAdmin'] && ORDER['order_status'] != 0) {
            $('#baljoo-send-area button.admin[data-act="save"]').show(); // (관리자)오더수정버튼 보이게
            if (ORDER['order_status'] < 5 && ORDER['order_type'] == 0) {
                $('#baljoo-send-area button.admin[data-act="send"]').show(); // (관리자)오더수정+재발주 보이게
            }
        }
        // 그외 일반적인 경우
        else {
            $('#baljoo-send-area .if-not-jijung').show();
            // 일지+수정모드일때
            if (editMode == 'myorderedit' && ORDER['order_type'] == 92) {
                $('#baljoo-send-area button[data-type="92"]').html('수정');
            }
        }

        this.print('sosok', true, this.sosoks);//소속본사
        this.print('car');//업종
        this.print('fee');//수수료여부
        // AI 발주는 도착시각이 90분 이내면 긴급으로 잡아 프리필에 담아 온다
        // (OrderPolicyService::isAiQuickTime). 여기서 60분 규칙으로 다시 판정하면 60~90분
        // 구간이 도로 꺼지므로 최초 1회만 건너뛴다. 도착일시를 손대는 순간부터는
        // 기존 규칙(offQuick / applyQuickTime)이 그대로 돈다.
        if (editMode != 'myorderedit' && !aiQuickFixed) {
            this.applyQuickTime();
        }
        this.print('quick');//긴급여부
        this.print('workday');//도착일자
        this.print('worktime');//도착시간
        this.print('howtopay');//결제책임
        this.print('howlong');//작업소요
        this.print('option');//기종옵션
        this.print('map');//현장주소
        this.print('contract_message');//전달사항
        this.print('contract_name');//거래처명
        this.print('contract_number');//거래처 전번
        this.print('contract_fieldnumber');//현장담당자 전번
        this.print('price');//가격
        this.checkSkyLowPrice();//스카이 저단가 판정 (경고 토스트 + 수수료없음)
        this.print('bonsa_col');//본사별 부가정보( 수임번호, 현장명 ... ) — meta(JSON) 에 저장
        this.print('meta');//메타데이터(머큐리 등)

        // 발주제한이 있는지 체크
        if (isLimited) {
            Util.swalert('발주가 제한되어 발주 하실 수 없습니다.');
        }
        // AI 발주 프리필 진입
        else if (editMode === 'ai') {
            // 가격 자동 계산 — 필수 입력이 채워졌으면 진입 즉시 1회 산정
            // ("금액을 설정해주세요" 안내가 뜨지 않도록)
            if (ORDER['car_type'] && ORDER['car_range'] && ORDER['car_workhour']) {
                this.calcPrice();
            }
            // 누락 단계 BS 자동 오픈 (GPS 빠진 슬롯 등을 사용자가 놓치지 않도록)
            setTimeout(() => this.openRequiredSlide(), 100);
        }
    }

    /**
     * 금액변경( 장비불러금액, 협력업체금액 )
     */
    async inputMultiPrice() {
        const self = this;
        // 칸이 둘이라 prompt(한 칸) 대신 html 슬롯에 직접 짜고 onConfirm 에서 읽는다
        // (구 Swal preConfirm 자리 — 닫히기 전에 불리므로 값이 살아있다).
        let p1 = null, p2 = null;
        await $j.Ui.Dialog.open({
            title: '금액 변경',
            html: `<label class="ui-dlg-inlabel" for="mp-jb">장비불러 금액</label>
<input type="number" id="mp-jb" class="ui-dlg-input" data-amount-kor value="${ORDER['car_price']}">
<label class="ui-dlg-inlabel" for="mp-bonsa" style="margin-top:10px">협력사 금액</label>
<input type="number" id="mp-bonsa" class="ui-dlg-input" data-amount-kor value="${ORDER['car_price_bonsa']}">`,
            confirm: '적용',
            cancel:  '취소',
            onConfirm: () => { p1 = $('#mp-jb').val(); p2 = $('#mp-bonsa').val(); },
        });
        if (p1 !== null) {
            ORDER['car_price'] = p1;
            ORDER['car_price_bonsa'] = p2;
            self.print('price');
        }
    }

    /**
     * 이벤트 핸들러 등록
     */
    initHandler() {
        const self = this;
        $j.Common.Form.registerPhoneHandler('input_contract_number');
        $j.Common.Form.registerPhoneHandler('input_contract_fieldnumber');
        // 소속별 이벤트 핸들러
        this.strategy && this.strategy.onInitHandler(this);
        $j.Common.Form.registerNumberHandler('input_price');// 장비대금 입력칸 자동컴마 처리
        if (G5['isAdmin']) {
            // 협력업체에서 발주하였고, 관리자라면 금액수정 팝업을 띄운다.
            if (!Util.isSosokJbbuller(ORDER['order_sosok']) && editMode == 'myorderedit') {
                $('#input_price').on('click', function () {
                    self.inputMultiPrice();
                });
            }
        } else {
            // 발주자,업체발주자는 금액 수정못하게
            if (['발주자', '업체발주자'].includes(MEMBER['mem_driver_type'])) {
                $('#input_price').attr('disabled', true);
            }
        }
        $('#input_price').on('focus', function () {
            self._priceOnFocus = $(this).val();
        });
        $('#input_price').on('blur', function () {
            // 금액을 **직접** 고쳤는지 표시한다. 금액은 사용자가 안 만져도 바뀌므로
            // (기종·작업소요·도착시간을 고치면 calcPrice 가 덮어쓴다) 수정 확인창이
            // '원래 금액으로 되돌릴까' 를 물을지 이걸로 가른다. 직접 친 값은 안 묻는다.
            if ($(this).val() !== self._priceOnFocus) self.priceEditedByUser = true;
            // 가격직접 입력한것 업데이트
            if (Util.isSosokJbbuller(ORDER['order_sosok'])) {
                ORDER['car_price'] = Util.parseNumber($('#input_price').val());
            } else {
                ORDER['car_price'] = Util.parseNumber($('#input_price').val());
                ORDER['car_price_bonsa'] = Util.parseNumber($('#input_price').val());
            }

            if (self.isLowOrderPrice()) {
                Util.swalert(['일정금액 이하는 발주할 수 없습니다.\n(단가를 지켜주세요.)', 'warning']);
            }
            self.checkSkyLowPrice();
        });

        // 회원 소속 테이블에서 값을 가져옴
        $('#input_sosok_area').on('click', function () {
            if (!sosoks.length) return; // 추가 소속이 없다면 스킵
            if ($(this).hasClass('on')) {
                $(this).removeClass('on');
            } else {
                $(this).addClass('on');
            }
        });
        // 소속변경시
        $('#input_sosok_area .item').on('click', function (e) {
            const code = $(this).attr('data-value');
            ORDER['order_sosok'] = code;
            self.updateHowtopayUI();

            self.print('sosok', false);
            self.calcPrice();
        });

        $('#input_fee').on('click', function () {
            self.toggleFee();
        });
        $('#input_quick').on('click', function () {
            self.toggleQuick();
        });
        $('#input_car_img, #input_car_txt').on('click', function () {
            self.openBS('car');
        });
        $('#input_workday, #input_workday_title').on('click', function () {
            self.openBS('workday');
        });
        $('#input_worktime, #input_worktime_title').on('click', function () {
            self.openBS('worktime');
        });
        $('#input_howtopay, #input_howtopay_title').on('click', function () {
            self.openBS('howtopay');
        });
        $('#input_howlong, #input_howlong_title').on('click', function () {
            self.openBS('howlong');
        });
        $('#input_option, #input_option_title').on('click', function () {
            self.openBS('option');
        });
        $('#input_address, #input_address_title').on('click', function () {
            self.openMap();
        });
        // 전화아이콘 클릭시, 지정 클릭시
        $('.input_phone').on('click', function () {
            let price = ORDER['car_price'];
            if (!ORDER['is_fee']) price = 0;
            const why = $(this).attr('data-value'); // 거래처,현장,지정
            $j.Ui.BS.open({
                target:      '$j.F.ModalPhone',
                fixedHeight: true,
                data:        {
                    $p:      {
                        from:             '발주',
                        why,
                        ordernum:         ORDER['num'],
                        // 내소속기사 기종매칭은 저장된 오더가 아니라 현재 선택값 기준
                        car_type:         ORDER['car_type'],
                        car_range:        ORDER['car_range'],
                        car_option:       ORDER['car_option'],
                        car_height:       ORDER['car_height'],
                        car_price:        price,
                        is_fee:           ORDER['is_fee'],
                        isRecentDisable:  MEMBER['mem_driver_type'] != '수발주자',
                        isFeeEditDisable: !Util.isSosokJbbuller(ORDER['order_sosok']),
                    },
                    onApply: ($r) => {
                        if (!ORDER.$r) ORDER.$r = {};
                        Object.assign(ORDER.$r, $r);
                        if (why == '거래처') {
                            ORDER['contract_number'] = $r['hp'];
                            ORDER['contract_name'] = $r['hp_name'];
                            self.print('contract_number');
                            self.print('contract_name');
                        } else if (why == '현장') {
                            ORDER['contract_fieldnumber'] = $r['hp'];
                            self.print('contract_fieldnumber');
                        } else if (why == '지정') {
                            ORDER['is_fee'] = $r['is_fee'];
                            self.print('jijung');
                            self.print('fee');
                            self.showJijungButton(true);
                        }
                    },
                },
            });
        });
        // 지정 삭제시
        $('#input_jijung_remove').on('click', function () {
            ORDER.$r = {};
            self.print('jijung');
            self.showJijungButton(false);
        });
        // 일지, 지정, 어플발주, 수정, 수정+재발주
        $('.apply_order').on('click', function () {
            let order_type = $(this).attr('data-type');
            if (order_type == '95') {
                order_type = ORDER.$r['order_type'];
                // 지정 대상이 해소되지 않은 채(ORDER.$r 비어있음) 작업지정이 눌리면
                // order_type 이 undefined 로 제출돼 서버에 NULL 오더가 박제되던 버그 차단.
                // 91(소속자)/94(외부)/95(타회원)만 허용, 아니면 지정 재선택 유도.
                if (!['91', '94', '95'].includes(String(order_type))) {
                    Util.swalert('지정 대상이 설정되지 않았습니다.\n지정 기사를 다시 선택해주세요.');
                    return;
                }
            }
            let edit_act = '';
            // 수정, 수정/재발주
            if (editMode == 'myorderedit') {
                edit_act = $(this).attr('data-act') == 'send' ? 'send' : 'save';

                const askEdit = () => self.confirmEdit(order_type, edit_act);

                // 저단가는 확인창보다 **먼저** 묻는다.
                // 수정 경로도 서버가 저단가 판정을 태워(OrderCreationService — 수정이면 원 발주시각
                // 기준) 하한 아래로 내리면 수수료없음으로 굳는다. 그런데 바로 다음에 뜨는
                // '금액 변경이 발생했습니다 [변경적용/금액되돌리기]' 가 되돌릴지를 고르는 자리다 —
                // 뒤에서 경고하면 이미 고른 뒤가 된다.
                // 수정 버튼 전체(예약 92 저장 포함)가 이 자리를 지나므로 여기 한 곳이면 된다.
                self.confirmSkyLow(askEdit);
                return;
            }
            // 신규발주
            else {
                self.checkSubmit(order_type);
            }
        });

        this.initAiFab();
    }

    /**
     * AI 발주 플로팅 버튼 (#ai-bal-fab) — 베타테스터/신규발주에서만 렌더됨.
     *
     * 모양·자리·끌어옮기기는 공용 $j.Ui.EdgeTab 이 맡는다 — 가장자리 세로중앙에 아이콘만 접혀 붙고,
     * 끌어서 상하로 옮기거나 반대편 가장자리에 붙일 수 있으며 그 자리가 기기에 남는다.
     * 관리자 복귀 알약과 같은 규격이다.
     *
     * 구: 우하단 큰 알약 + $j.Ui.FabHide(오른쪽으로 밀어 숨기기). 가리는 면적 자체를 줄이는 쪽이
     *     나아서 걷어냈다 — 접힌 탭은 34px 라 밀어 치울 일이 거의 없고, 페이지마다 걸리는 것이
     *     다른 문제는 숨김이 아니라 '옮길 수 있음'이 푼다. FabHide 모듈 자체는 일지 FAB 이 계속 쓴다.
     * 구: 제출영역(#baljoo-send-area)이 올라오면 bottom 을 동적으로 밀어올려 가림 방지 → 스크롤 시
     *     버튼이 움직여 '고정이 아닌 것처럼' 보였다. 세로중앙 고정으로 그 문제도 없어졌다.
     */
    initAiFab() {
        const fab = document.getElementById('ai-bal-fab');
        if (!fab) return;

        // 기본자리는 화면 하단 — 제출영역(#baljoo-send-area, '장비불러!')이 고정이 아니라 폼 끝의
        // 흐름 요소라, 스크롤을 내리면 화면 아래쪽에 온다. 그 버튼 바로 위에 앉힌다.
        // 세로중앙을 비워 두므로 대리진입 중 관리자 복귀 알약과도 만나지 않는다.
        // (슬롯은 '옮기기 전 기본자리'다 — 끌어서 옮겼으면 저장된 자리가 이긴다)
        $j.Ui.EdgeTab.attach(fab, {
            key:  'aiBal',
            slot: 'bottom',
            onTap: () => {
                $j.Ui.BS.open({
                    target:      '$j.F.Baljoo.ModalAiChat',
                    fixedHeight: true,
                    headerBg:    'gray',
                    layout:      'classic',
                });
            },
        });
    }

    // ──────────────────────────────────────────────────────────
    // Render / Submit 위임
    // ──────────────────────────────────────────────────────────

    print            = (id, isFirst = false) => this.renderer.print(id, isFirst);
    printSosok       = (isFirst) => this.renderer.printSosok(isFirst);
    findPlaceName    = (msg, place) => this.renderer.findPlaceName(msg, place);
    openMap          = () => this.renderer.openMap();
    updateHowtopayUI = () => this.renderer.updateHowtopayUI();
    showJijungButton = (flag = true) => this.renderer.showJijungButton(flag);

    isAllFilled        = () => this.submitter.isAllFilled();
    isHowtopaySet      = () => this.submitter.isHowtopaySet();
    isValidateWorktime = (order_type) => this.submitter.isValidateWorktime(order_type);
    isValidateWorkhour = (order_type) => this.submitter.isValidateWorkhour(order_type);
    isLowOrderPrice    = () => this.submitter.isLowOrderPrice();
    isSkyLowPrice      = () => this.submitter.isSkyLowPrice();
    confirmSkyLow      = (next) => this.submitter.confirmSkyLow(next);
    confirmEdit        = (order_type, edit_act) => this.submitter.confirmEdit(order_type, edit_act);
    checkSubmit        = (order_type, edit_act = '') => this.submitter.checkSubmit(order_type, edit_act);
    submit             = () => this.submitter.submit();
    waitGo             = (msg, url, t) => this.submitter.waitGo(msg, url, t);
}
